import propTypes from "prop-types";
import { useState } from "react";

import styled from "styled-components/macro";

import GivingSettingsError from "Giving/FundItSettings/screens/Components/GivingSettingsError";
import { AnimatePresence, motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import TextEditorHoverMenuVariant from "UI/FnrTextEditiorHoverMenu/TextEditorHoverMenuVariant";

import { getTextIsOverLimit } from "../utils/getTextIsOverLimit";

import DescriptionConfirmCancel from "./DescriptionConfirmCancel";

export default function DescriptionEditor({
  value,
  setValue,
  isLoading,
  hasError,
  submitDescription,
  onCancel,
  maxCharacters,
  autofocus,
  savedSuccessfully,
  lightMode,
  editorRef,
  editor,
}) {
  const { isOverLimit, charactersLeft, valueTextOnly } = getTextIsOverLimit(
    value,
    maxCharacters
  );

  const [hasOverLimitError, setHasOverLimitError] = useState(false);

  const pluralize = charactersLeft === 1 || charactersLeft === -1 ? "" : "s";
  const charactersLeftText = `${charactersLeft} character${pluralize} left`;
  const overLimitText = `${-charactersLeft} character${pluralize} over the limit`;

  const handleSave = () => {
    if (isOverLimit) {
      setHasOverLimitError(true);
      return;
    }
    setHasOverLimitError(false);
    submitDescription();
  };

  return (
    <>
      <EditorWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        $hasError={hasError || hasOverLimitError}
      >
        <STextEditorHoverMenu
          editorRef={editorRef}
          editor={editor}
          editorDataCy="Signup_Description_Editor"
          lineHeight="1.2rem"
          minHeight="140px"
          maxHeight="400px"
          maxLength={maxCharacters}
          textInternal={value}
          setTextInternal={setValue}
          characterLimit={maxCharacters}
          autofocus={autofocus}
          placeholder="Add a description..."
          $lightMode={lightMode}
          menuStyles={{ boxShadow: "rgba(0, 0, 0, 0.2) 0 1px 6px" }}
          renderSaveButton={(editorIsFocused) => (
            <ConfirmCancelOuter>
              <SFnrConfirmCancel
                cancel={onCancel}
                confirm={handleSave}
                isLoading={isLoading}
                editorFocused={editorIsFocused}
                $focused={editorIsFocused}
                savedSuccessfully={savedSuccessfully}
                isOverLimit={isOverLimit}
              >
                <AnimatePresence>
                  {charactersLeft <= 50 ? (
                    <CharactersLeft
                      $isOverLimit={isOverLimit}
                      key="charLeft"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 0.75, height: isOverLimit ? 32 : 16 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {isOverLimit ? (
                        <FnrIcon icon="fa-exclamation-triangle fas" />
                      ) : null}
                      {isOverLimit ? overLimitText : charactersLeftText}
                    </CharactersLeft>
                  ) : null}
                </AnimatePresence>
              </SFnrConfirmCancel>
            </ConfirmCancelOuter>
          )}
        />

        <GivingSettingsError
          hasError={hasError}
          error="Error saving description!"
          style={{ alignSelf: "center" }}
        />
        <GivingSettingsError
          hasError={hasOverLimitError}
          error="Please shorten your description. You are over the character limit."
          style={{ alignSelf: "center" }}
        />
      </EditorWrapper>
    </>
  );
}

DescriptionEditor.propTypes = {
  isLoading: propTypes.bool.isRequired,
  hasError: propTypes.bool.isRequired,
  value: propTypes.string.isRequired,
  setValue: propTypes.func.isRequired,
  submitDescription: propTypes.func.isRequired,
  onCancel: propTypes.func.isRequired,
  maxCharacters: propTypes.number.isRequired,
  autofocus: propTypes.bool,
  savedSuccessfully: propTypes.bool,
  lightMode: propTypes.bool,
  editorRef: propTypes.object,
  editor: propTypes.object,
};

const EditorWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: ${(p) => (p.$hasError ? "36px" : "0px")};
  transition: margin-bottom 300ms;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.2) 0 1px 6px;

  @media screen and (max-width: 600px) {
    margin-bottom: ${(p) => (p.$hasError ? "60px" : "0px")};
  }
`;

const STextEditorHoverMenu = styled(TextEditorHoverMenuVariant)`
  padding: 10px 10px 0px 10px;
  border-radius: 8px;

  &:focus-within {
    outline: 2px solid hsl(0 0% 100% / 1);
    outline-offset: -2px;
  }

  & .ProseMirror p.is-editor-empty:first-child::before {
    color: ${(p) => (p.$lightMode ? "#383838" : "#eceff0")};
    opacity: 0.75;
  }
`;

const ConfirmCancelOuter = styled.div`
  position: relative;
  overflow: hidden;
  padding-bottom: 10px;
`;

const CharactersLeft = styled(motion.div)`
  font-size: ${({ $isOverLimit }) => ($isOverLimit ? "1rem" : ".8125rem")};
  margin-top: 2px;
  text-align: right;
  color: ${({ $isOverLimit }) => ($isOverLimit ? "#ecc27b" : "inherit")};
  display: flex;
  align-items: center;
  gap: 6px;

  @media screen and (max-width: 500px) {
    flex-grow: ${({ $isOverLimit }) => ($isOverLimit ? "1" : "0")};
    justify-content: ${({ $isOverLimit }) =>
      $isOverLimit ? "flex-end" : "center"};
    text-align: left;
  }
`;

const SFnrConfirmCancel = styled(DescriptionConfirmCancel)`
  position: relative;
  margin-top: 8px;
  pointer-events: ${(p) => (p.$focused ? "all" : "none")};
`;